import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cfpBaseUrl } from '../../network/urls';

// ✅ Function to convert date format
const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const initialState = {
    data: [],
    pageNumber: 1,
    pageSize: 20,
    dateFrom: '',
    dateTo: '',
    officeCode: '',
    isLoading: false,
    totalPages: null,
    totalRecords: null,
    error: null,
};

export const fetchSRDataForEmail = createAsyncThunk(
    'srDataForEmail/fetchSRDataForEmail',
    async ({ pageNumber, pageSize, dateFrom, dateTo , officeCode }, { rejectWithValue, getState }) => {
        const { auth } = getState();
        const { token } = auth;

        try {
            const myHeaders = new Headers();
            myHeaders.append('Authorization', token);
            myHeaders.append('Content-Type', 'application/json');

            const formattedDateFrom = formatDate(dateFrom);
            const formattedDateTo = formatDate(dateTo);

            const raw = JSON.stringify({
                pageNumber,
                pageSize,
                "dateFrom": formattedDateFrom,
                "dateTo": formattedDateTo,
                "officeCode" : officeCode

            });

            const requestOptions = {
                method: 'POST',
                headers: myHeaders,
                body: raw,
                redirect: 'follow',
            };

            const response = await fetch(cfpBaseUrl + 'enc/getSRDataForEmail', requestOptions);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch SR Data!');
            }

            const result = await response.json();

            if (result.Message === 'Data Not Found.') {
                return { data: [], totalPages: 0, totalRecords: 0 };
            }

            return {
                data: result.data || result.Message || [],
                totalPages: result.totalPages || 1,
                totalRecords: result.totalRecords || 0,
            };
        } catch (error) {
            return rejectWithValue(error.message || 'An error occurred while fetching SR data.');
        }
    }
);

const getSRDataForEmailSlice = createSlice({
    name: 'srDataForEmail',
    initialState,
    reducers: {
        nextPage(state) {
            state.pageNumber += 1;
        },
        previousPage(state) {
            if (state.pageNumber > 1) state.pageNumber -= 1;
        },
        resetState(state) {
            state.data = [];
            state.pageNumber = 1;
            state.pageSize = 20;
            state.dateFrom = '';
            state.dateTo = '';
            state.isLoading = false;
            state.totalPages = null;
            state.totalRecords = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSRDataForEmail.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSRDataForEmail.fulfilled, (state, action) => {
                state.isLoading = false;
                state.data = action.payload.data || [];
                state.totalPages = action.payload.totalPages || 1;
                state.totalRecords = action.payload.totalRecords || 0;
                state.pageNumber = action.meta.arg.pageNumber;
                state.pageSize = action.meta.arg.pageSize;
                state.dateFrom = action.meta.arg.dateFrom;
                state.dateTo = action.meta.arg.dateTo;
                state.error = null;
            })
            .addCase(fetchSRDataForEmail.rejected, (state, action) => {
                state.isLoading = false;
                state.data = [];
                state.totalPages = 0;
                state.totalRecords = 0;
                state.error = action.payload || 'Failed to fetch SR data.';
            });
    },
});

export const { nextPage, previousPage, resetState } = getSRDataForEmailSlice.actions;
export default getSRDataForEmailSlice.reducer;
